import type {
  MarketingFrame,
  MarketingMotion,
  MarketingViewport,
  SubsystemSnapshot,
} from "../../types";
import type { WorldObjectIdentities } from "../../world";
import { sampleShareSequence, type ShareSequenceFrame } from "./sequence";
import {
  SOCIAL_SHARE_PLATFORM_IDS,
  SOCIAL_SHARE_PLATFORM_OUTCOMES,
  SOCIAL_SHARE_SOURCE_CONTRACT,
  type SocialSharePlatform,
} from "./sourceContract";

export type SocialShareProofPhase =
  | "approach"
  | "preview"
  | "save"
  | "copy"
  | "x-composer"
  | "telegram-desktop"
  | "instagram-home"
  | "reddit-composer"
  | "complete";

export type SocialShareAction = "save" | "copy" | "other";
export type SocialShareOutcomeState = "pending" | "active" | "resolved";

export interface SocialShareProofState {
  readonly phase: SocialShareProofPhase;
  readonly modalProgress: number;
  readonly worldTransfer: number;
  readonly worldPreviewActive: boolean;
  readonly activeAction: SocialShareAction | null;
  readonly activePlatform: SocialSharePlatform | null;
  readonly otherWaysVisible: boolean;
  readonly productStatus: string;
  readonly outcomeStates: Readonly<Record<SocialSharePlatform, SocialShareOutcomeState>>;
}

export interface SocialShareProofSnapshot extends SubsystemSnapshot, SocialShareProofState {
  readonly state: "managed";
  readonly sequence: ShareSequenceFrame;
  readonly headingClip: SocialSharePreviewClip;
  readonly previewClip: SocialSharePreviewClip;
  readonly previewMode: "shared-world" | "static-fallback";
  readonly motion: MarketingMotion;
  readonly viewport: MarketingViewport;
  readonly modal: {
    readonly visualState: "closed" | "opening" | "open";
    readonly ariaHidden: boolean;
    readonly stagedFocusableCount: number;
    readonly accessibleName: string;
  };
}

export interface SocialSharePreviewClip {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface SocialShareWorldPreviewInput {
  readonly objects: WorldObjectIdentities;
  readonly clip: SocialSharePreviewClip;
  readonly transferProgress: number;
  readonly phase: SocialShareProofPhase;
  readonly sequence?: ShareSequenceFrame;
  readonly headingClip?: SocialSharePreviewClip;
}

export interface SocialShareWorldPort {
  readonly objects: WorldObjectIdentities;
  applySharePreview(input: SocialShareWorldPreviewInput): void;
  clearSharePreview(): void;
}

export interface SocialShareProofOptions {
  readonly world?: SocialShareWorldPort;
  readonly nativeShareSupported?: boolean;
}

const clamp = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const smoothstep = (value: number): number => {
  const amount = clamp(value);
  return amount * amount * (3 - 2 * amount);
};

const phaseFor = (progress: number): SocialShareProofPhase => {
  if (progress < 0.14) return "approach";
  if (progress < 0.30) return "preview";
  if (progress < 0.41) return "save";
  if (progress < 0.52) return "copy";
  if (progress < 0.63) return "x-composer";
  if (progress < 0.74) return "telegram-desktop";
  if (progress < 0.85) return "instagram-home";
  if (progress < 0.96) return "reddit-composer";
  return "complete";
};

const activePlatformFor = (phase: SocialShareProofPhase): SocialSharePlatform | null => {
  if (phase === "x-composer") return "x";
  if (phase === "telegram-desktop") return "telegram";
  if (phase === "instagram-home") return "instagram";
  if (phase === "reddit-composer") return "reddit";
  return null;
};

const activeActionFor = (phase: SocialShareProofPhase): SocialShareAction | null => {
  if (phase === "save") return "save";
  if (phase === "copy") return "copy";
  return null;
};

const productStatusFor = (phase: SocialShareProofPhase): string => {
  if (phase === "save") return SOCIAL_SHARE_SOURCE_CONTRACT.statuses.saved;
  if (phase === "copy") return SOCIAL_SHARE_SOURCE_CONTRACT.statuses.copied;
  if (activePlatformFor(phase)) return SOCIAL_SHARE_SOURCE_CONTRACT.statuses.opened;
  return "";
};

const outcomeStatesFor = (phase: SocialShareProofPhase): Readonly<Record<SocialSharePlatform, SocialShareOutcomeState>> => {
  const active = activePlatformFor(phase);
  const activeIndex = active ? SOCIAL_SHARE_PLATFORM_IDS.indexOf(active) : -1;
  const complete = phase === "complete";
  return Object.freeze(Object.fromEntries(SOCIAL_SHARE_PLATFORM_IDS.map((platform, index) => [
    platform,
    complete || (activeIndex >= 0 && index < activeIndex)
      ? "resolved"
      : platform === active
        ? "active"
        : "pending",
  ])) as Record<SocialSharePlatform, SocialShareOutcomeState>);
};

export function sampleSocialShareProofState(
  progress: number,
  motion: MarketingMotion,
  nativeShareSupported: boolean,
  worldPreviewActive = true,
): SocialShareProofState {
  const value = motion === "reduced" ? 1 : clamp(progress);
  const phase = phaseFor(value);
  return {
    phase,
    modalProgress: motion === "reduced" ? 1 : smoothstep((value - 0.18) / 0.12),
    worldTransfer: worldPreviewActive
      ? motion === "reduced" ? 1 : smoothstep(value / 0.14)
      : 0,
    worldPreviewActive,
    activeAction: activeActionFor(phase),
    activePlatform: activePlatformFor(phase),
    otherWaysVisible: nativeShareSupported,
    productStatus: productStatusFor(phase),
    outcomeStates: outcomeStatesFor(phase),
  };
}

const xIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21.308L14.615 9.653L22.488 22H16.322L11.492 14.498L4.928 22H1.862L9.021 13.819L1.472 2H7.794L12.16 8.846L18.244 2ZM17.168 20.052H18.866L6.871 3.846H5.047L17.168 20.052Z" /></svg>';
const telegramIcon = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.6 5.4L17.8 18.6C17.6 19.5 17.1 19.8 16.3 19.4L12.4 16.6L10.5 18.5C10.3 18.7 10.1 18.9 9.7 18.9L10 14.9L17.3 8.3C17.6 8 17.2 7.8 16.8 8.1L7.8 13.8L4 12.6C3.2 12.3 3.2 11.8 4.2 11.4L19 5.7C19.7 5.4 20.4 5.8 20.6 5.4Z" fill="currentColor" /></svg>';
const instagramIcon = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" stroke-width="2" /><circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="2" /><circle cx="17.2" cy="6.8" r="1" fill="currentColor" /></svg>';
const redditIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614.028.17.042.344.042.52 0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249C10.498 12.561 9.937 12 9.25 12zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249C16 12.562 15.438 12 14.75 12zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z" /></svg>';

const platformIcon = (platform: SocialSharePlatform): string => ({
  x: xIcon,
  telegram: telegramIcon,
  instagram: instagramIcon,
  reddit: redditIcon,
})[platform];

export function renderSocialShareProofMarkup(): string {
  const contract = SOCIAL_SHARE_SOURCE_CONTRACT;
  const platformMarkup = SOCIAL_SHARE_PLATFORM_IDS.map((platform) => {
    const outcome = SOCIAL_SHARE_PLATFORM_OUTCOMES[platform];
    return `<span class="share-proof__platform share-proof__platform--${platform}" data-share-platform="${platform}" title="${outcome.label}" aria-hidden="true">${platformIcon(platform)}</span>`;
  }).join("");
  return `
    <div class="share-proof" data-share-proof role="group" aria-label="Social Share из Blender Bridge Desktop ${contract.sourceRelease}">
      <div class="share-proof__modal" data-share-modal aria-hidden="true">
        <span class="share-proof__close">
          <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2L10 10M10 2L2 10" /></svg>
        </span>
        <div class="share-proof__title">${contract.title}</div>
        <div class="share-proof__preview" data-shared-world-preview>
          <span data-share-static-fallback aria-hidden="true"></span>
        </div>
        <div class="share-proof__actions">
          <span class="share-proof__action" data-share-action="save">${contract.actions.save}</span>
          <span class="share-proof__action" data-share-action="copy">${contract.actions.copy}</span>
          <span class="share-proof__action" data-share-action="other" hidden>${contract.actions.other}</span>
        </div>
        <div class="share-proof__hint">${contract.hint}</div>
        <div class="share-proof__platforms">${platformMarkup}</div>
        <div class="share-proof__product-status" data-share-product-status hidden></div>
      </div>
    </div>`;
}

interface SlotState {
  readonly node: HTMLElement;
  readonly html: string;
  readonly hadFallback: boolean;
}

const finite = (value: number): number => Number.isFinite(value) ? value : 0;
const EMPTY_PREVIEW_CLIP: SocialSharePreviewClip = Object.freeze({ left: 0, top: 0, width: 0, height: 0 });
const STAGED_FOCUSABLE_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "select",
  "textarea",
  "[tabindex]:not([tabindex='-1'])",
].join(",");
const clipFrom = (node: HTMLElement): SocialSharePreviewClip => {
  const rect = node.getBoundingClientRect();
  return {
    left: finite(rect.left),
    top: finite(rect.top),
    width: Math.max(0, finite(rect.width)),
    height: Math.max(0, finite(rect.height)),
  };
};

export class SocialShareProofController {
  private readonly slot: SlotState;
  private readonly world?: SocialShareWorldPort;
  private readonly nativeShareSupported: boolean;
  private readonly proof: HTMLElement;
  private readonly modal: HTMLElement;
  private readonly preview: HTMLElement;
  private readonly section: HTMLElement | null;
  private readonly heading: HTMLElement | null;
  private readonly copy: HTMLElement | null;
  private readonly originalCopy: string | null;
  private sequence = sampleShareSequence(0, "full");
  private currentPreviewClip: SocialSharePreviewClip = EMPTY_PREVIEW_CLIP;
  private current: SocialShareProofState;
  private worldPreviewPublished = false;
  private destroyed = false;

  constructor(root: ParentNode, options: SocialShareProofOptions = {}) {
    const node = root.querySelector<HTMLElement>('[data-proof-slot="social-share"]');
    if (!node) throw new Error("SocialShareProofController requires the social-share proof slot.");
    this.slot = {
      node,
      html: node.innerHTML,
      hadFallback: node.hasAttribute("data-proof-fallback"),
    };
    this.world = options.world;
    this.nativeShareSupported = options.nativeShareSupported === true;
    this.section = root.querySelector<HTMLElement>("#share");
    this.copy = this.section?.querySelector<HTMLElement>(".marketing-copy") ?? null;
    this.originalCopy = this.copy?.innerHTML ?? null;
    if (this.copy) this.copy.innerHTML = `<h2 id="share-title">Делись своими работами</h2><ul class="share-reasons"><li>Показывай результат</li><li>Собирай обратную связь</li><li>Находи новые знакомства и проекты</li></ul>`;
    this.heading = this.copy?.querySelector<HTMLElement>("h2") ?? null;
    this.section?.setAttribute("data-share-directed", "true");

    node.innerHTML = renderSocialShareProofMarkup();
    const proof = node.querySelector<HTMLElement>("[data-share-proof]");
    const modal = node.querySelector<HTMLElement>("[data-share-modal]");
    const preview = node.querySelector<HTMLElement>("[data-shared-world-preview]");
    if (!proof || !modal || !preview) throw new Error("Social Share proof structure is incomplete.");
    this.proof = proof;
    this.modal = modal;
    this.preview = preview;
    this.current = sampleSocialShareProofState(0, "full", this.nativeShareSupported, false);

    node.removeAttribute("data-proof-fallback");
    node.dataset.proofEnhanced = "social-share";
    this.apply(this.current, false);
  }

  render(frame: MarketingFrame, _now?: number): SocialShareProofSnapshot {
    if (this.destroyed) throw new Error("SocialShareProofController has been destroyed.");
    const clock = this.section?.dataset ?? this.slot.node.dataset;
    this.sequence = sampleShareSequence(Number(clock.shareElapsedMs ?? 0), frame.motion, Number(clock.shareExitProgress ?? 0));
    const worldPreviewActive = frame.activeSection === "share";
    const id = this.sequence.activeControl?.id;
    const phase: SocialShareProofPhase = id === "x" ? "x-composer"
      : id === "telegram" ? "telegram-desktop" : id === "instagram" ? "instagram-home"
        : id === "reddit" ? "reddit-composer" : id ?? (this.sequence.phase === "complete" || this.sequence.phase === "exit"
          ? "complete" : this.sequence.modalPresence > 0 ? "preview" : "approach");
    this.current = {
      phase,
      modalProgress: worldPreviewActive ? this.sequence.modalPresence : 0,
      worldTransfer: worldPreviewActive ? this.sequence.cubeTransfer : 0,
      worldPreviewActive,
      activeAction: id === "save" || id === "copy" ? id : null,
      activePlatform: id && id !== "save" && id !== "copy" ? id : null,
      otherWaysVisible: this.nativeShareSupported,
      productStatus: "",
      outcomeStates: outcomeStatesFor(phase),
    };
    this.apply(this.current, true);
    const modalProgress = this.current.modalProgress;
    return {
      state: "managed",
      ...this.current,
      sequence: this.sequence,
      headingClip: this.heading ? clipFrom(this.heading) : EMPTY_PREVIEW_CLIP,
      previewClip: this.currentPreviewClip,
      previewMode: this.world && this.current.worldPreviewActive ? "shared-world" : "static-fallback",
      motion: frame.motion,
      viewport: frame.viewport,
      modal: {
        visualState: modalProgress <= 0 ? "closed" : modalProgress >= 1 ? "open" : "opening",
        ariaHidden: this.modal.getAttribute("aria-hidden") === "true",
        stagedFocusableCount: this.modal.querySelectorAll<HTMLElement>(STAGED_FOCUSABLE_SELECTOR).length,
        accessibleName: this.proof.getAttribute("aria-label")?.trim() ?? "",
      },
    };
  }

  resize(viewport: MarketingViewport): void {
    if (this.destroyed) return;
    this.slot.node.dataset.proofViewport = viewport;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.worldPreviewPublished) this.world?.clearSharePreview();
    this.worldPreviewPublished = false;
    this.slot.node.innerHTML = this.slot.html;
    if (this.copy && this.originalCopy !== null) this.copy.innerHTML = this.originalCopy;
    this.section?.removeAttribute("data-share-directed");
    this.section?.removeAttribute("data-share-sequence-phase");
    this.section?.removeAttribute("data-share-capture-still");
    this.section?.style.removeProperty("--share-heading-presence");
    this.section?.style.removeProperty("--share-reasons-presence");
    delete this.slot.node.dataset.proofEnhanced;
    delete this.slot.node.dataset.proofViewport;
    delete this.slot.node.dataset.sharePhase;
    delete this.slot.node.dataset.shareSequencePhase;
    this.slot.node.removeAttribute("data-world-attached");
    this.slot.node.toggleAttribute("data-proof-fallback", this.slot.hadFallback);
    this.slot.node.style.removeProperty("--share-modal-progress");
    this.slot.node.style.removeProperty("--share-world-transfer");
    this.slot.node.style.removeProperty("--share-capture-flash");
  }

  private apply(state: SocialShareProofState, publishWorld: boolean): void {
    const node = this.slot.node;
    node.dataset.sharePhase = state.phase;
    node.dataset.shareSequencePhase = this.sequence.phase;
    if (this.section) {
      this.section.dataset.shareSequencePhase = this.sequence.phase;
      this.section.dataset.shareCaptureStill = String(this.sequence.captureStill);
      this.section.style.setProperty("--share-heading-presence", this.sequence.headingPresence.toFixed(4));
      this.section.style.setProperty("--share-reasons-presence", this.sequence.reasonsPresence.toFixed(4));
    }
    node.style.setProperty("--share-modal-progress", state.modalProgress.toFixed(4));
    node.style.setProperty("--share-world-transfer", state.worldTransfer.toFixed(4));
    node.style.setProperty("--share-capture-flash", this.sequence.captureFlash.toFixed(4));
    node.toggleAttribute("data-world-attached", Boolean(this.world && state.worldPreviewActive));

    node.querySelectorAll<HTMLElement>("[data-share-action]").forEach((action) => {
      action.toggleAttribute("data-active", action.dataset.shareAction === state.activeAction);
      action.style.setProperty("--share-control-press", action.dataset.shareAction === state.activeAction ? String(this.sequence.activeControl?.press ?? 0) : "0");
      action.style.setProperty("--share-control-hover", action.dataset.shareAction === state.activeAction ? String(this.sequence.activeControl?.hover ?? 0) : "0");
      if (action.dataset.shareAction === "other") action.toggleAttribute("hidden", !state.otherWaysVisible);
    });
    node.querySelectorAll<HTMLElement>("[data-share-platform]").forEach((platform) => {
      platform.toggleAttribute("data-active", platform.dataset.sharePlatform === state.activePlatform);
      platform.style.setProperty("--share-control-press", platform.dataset.sharePlatform === state.activePlatform ? String(this.sequence.activeControl?.press ?? 0) : "0");
      platform.style.setProperty("--share-control-hover", platform.dataset.sharePlatform === state.activePlatform ? String(this.sequence.activeControl?.hover ?? 0) : "0");
    });
    node.querySelectorAll<HTMLElement>("[data-share-outcome]").forEach((outcome) => {
      const platform = outcome.dataset.shareOutcome as SocialSharePlatform;
      outcome.setAttribute("data-share-outcome-state", state.outcomeStates[platform] ?? "pending");
    });
    const status = node.querySelector<HTMLElement>("[data-share-product-status]");
    if (status) {
      status.textContent = state.productStatus;
      status.toggleAttribute("hidden", !state.productStatus);
    }

    if (publishWorld) this.currentPreviewClip = clipFrom(this.preview);

    if (publishWorld && this.world && state.worldPreviewActive) {
      this.world.applySharePreview({
        objects: this.world.objects,
        clip: this.currentPreviewClip,
        transferProgress: state.worldTransfer,
        phase: state.phase,
        sequence: this.sequence,
        headingClip: this.heading ? clipFrom(this.heading) : EMPTY_PREVIEW_CLIP,
      });
      this.worldPreviewPublished = true;
    } else if (publishWorld && this.world && this.worldPreviewPublished) {
      this.world.clearSharePreview();
      this.worldPreviewPublished = false;
    }
  }
}
